import React, { useEffect } from 'react';
import {
  Router,
  navigate,
  RouteComponentProps,
  useLocation,
} from '@reach/router';
import ReactGA from 'react-ga';

import { refreshToken } from 'utils/push-notifications';
import Login from 'components/pages/Login';
import LoadingIndicator from 'components/shared/LoadingIndicator';
import RooftopPage from './RooftopPage';
import {
  useSwitchRooftopMutation,
  useToken,
  useUpdateDeviceTokenMutation,
} from 'api';
import { useCurrentLocationId } from 'hooks';

function useNavigateToDefaultLocation() {
  const { data } = useToken();
  const locationId = useCurrentLocationId();
  if (data !== undefined && locationId === undefined) {
    navigate(`/${data.location.id}`);
  }
}

function useSendPageviews() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Send pageview without ids to analytics
    const pageView = pathname?.replace(
      /\/[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}/g,
      ''
    );
    if (window.recon.gtmInit && !!pageView) {
      ReactGA.pageview(pageView);
    }
  }, [pathname]);
}

const UserSessionRouter: React.FC<RouteComponentProps> = () => {
  useNavigateToDefaultLocation();
  useSendPageviews();
  const { mutateAsync: switchRooftop, isLoading: isSwitchingRooftop } =
    useSwitchRooftopMutation();
  const locationId = useCurrentLocationId();

  useEffect(() => {
    locationId && switchRooftop();
  }, [locationId, switchRooftop]);

  const {
    data: userData,
    isLoading: isLoadingUserData,
    isError: useTokenIsError,
  } = useToken();

  const { mutateAsync: updateDeviceToken } = useUpdateDeviceTokenMutation();
  useEffect(() => {
    if (!useTokenIsError) {
      refreshToken(updateDeviceToken);
    }
  });

  if (useTokenIsError) {
    return <Login />;
  }

  if (!locationId || userData === undefined) return null;

  return (
    <div className="page flex-rows">
      {isSwitchingRooftop || isLoadingUserData ? (
        <LoadingIndicator />
      ) : (
        <Router>
          <RooftopPage path=":rooftopId/*" />
        </Router>
      )}
    </div>
  );
};

export default UserSessionRouter;
